import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import { User as FirebaseUser, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../shared/lib/firebase';
import { authService, AuthUser } from '../../shared/services/authService';

export interface AuthState {
  user: AuthUser | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  /** Firebase Auth emailVerified flag. Separate from the user doc so it can refresh without a new sign-in. */
  emailVerified: boolean;
}

export interface AuthActions {
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signUp: (
    email: string,
    password: string,
    name: string,
    phone?: string
  ) => Promise<{ success: boolean; error?: string; verificationEmailSent?: boolean }>;
  signOut: () => Promise<{ success: boolean; error?: string }>;
  resetPassword: (
    email: string
  ) => Promise<{ success: boolean; error?: string; message?: string }>;
  requestEmailVerification: () => Promise<{ success: boolean; error?: string; message?: string }>;
  /** Reload the Auth user so emailVerified updates after the verification link is used. */
  refreshEmailVerified: () => Promise<void>;
  updateProfile: (updates: Partial<AuthUser>) => Promise<{ success: boolean; error?: string }>;
}

const AuthStateContext = createContext<AuthState | undefined>(undefined);
const AuthActionsContext = createContext<AuthActions | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);
      setEmailVerified(fbUser?.emailVerified ?? false);
      if (fbUser) {
        try {
          const userData = await authService.getCurrentUserData();
          if (userData) {
            setUser(userData);
            setIsAdmin(userData.role === 'admin' || userData.role === 'super_admin');
            setIsSuperAdmin(userData.role === 'super_admin');
          } else {
            // Signup writes the profile just after the auth user is created.
            // Keep a profile already stored for this account instead of wiping it.
            setUser((prev) => (prev?.uid === fbUser.uid ? prev : null));
            setIsAdmin(false);
            setIsSuperAdmin(false);
          }
        } catch {
          setUser(null);
          setIsAdmin(false);
        }
      } else {
        setUser(null);
        setIsAdmin(false);
        setIsSuperAdmin(false);
        setEmailVerified(false);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const refreshEmailVerified = useCallback(async () => {
    const current = auth.currentUser;
    if (!current) {
      setEmailVerified(false);
      return;
    }
    await current.reload();
    await current.getIdToken(true);
    setEmailVerified(current.emailVerified);
  }, []);

  // The verification link often opens in another tab. Refresh when they come back.
  useEffect(() => {
    if (!user?.verificationRequired || emailVerified) return;
    const onFocus = () => {
      refreshEmailVerified().catch(() => {});
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [user?.verificationRequired, emailVerified, refreshEmailVerified]);

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await authService.signIn(email, password);
    return { success: result.success, error: result.error?.message };
  }, []);

  const signUp = useCallback(
    async (email: string, password: string, name: string, phone?: string) => {
      const result = await authService.signUp(email, password, name, phone);
      if (result.success && result.user) {
        setUser(result.user);
        setEmailVerified(auth.currentUser?.emailVerified ?? false);
      }
      return {
        success: result.success,
        error: result.error?.message,
        verificationEmailSent: result.verificationEmailSent,
      };
    },
    []
  );

  const signOut = useCallback(async () => {
    const result = await authService.signOut();
    return { success: result.success, error: result.error?.message };
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const result = await authService.resetPassword(email);
    return {
      success: result.success,
      error: result.error?.message,
      message: result.message,
    };
  }, []);

  const requestEmailVerification = useCallback(async () => {
    const result = await authService.requestEmailVerification();
    if (result.success) {
      await refreshEmailVerified();
    }
    return {
      success: result.success,
      error: result.error?.message,
      message: result.message,
    };
  }, [refreshEmailVerified]);

  const updateProfile = useCallback(async (updates: Partial<AuthUser>) => {
    const result = await authService.updateUserProfile(updates);
    if (result.success) {
      setUser((prev) => (prev ? { ...prev, ...updates } : null));
    }
    return { success: result.success, error: result.error?.message };
  }, []);

  const stateValue = useMemo<AuthState>(
    () => ({ user, firebaseUser, loading, isAdmin, isSuperAdmin, emailVerified }),
    [user, firebaseUser, loading, isAdmin, isSuperAdmin, emailVerified]
  );

  const actionsValue = useMemo<AuthActions>(
    () => ({
      signIn,
      signUp,
      signOut,
      resetPassword,
      requestEmailVerification,
      refreshEmailVerified,
      updateProfile,
    }),
    [signIn, signUp, signOut, resetPassword, requestEmailVerification, refreshEmailVerified, updateProfile]
  );

  return (
    <AuthStateContext.Provider value={stateValue}>
      <AuthActionsContext.Provider value={actionsValue}>
        {children}
      </AuthActionsContext.Provider>
    </AuthStateContext.Provider>
  );
};

export function useAuthState(): AuthState {
  const context = useContext(AuthStateContext);
  if (context === undefined) {
    throw new Error('useAuthState must be used within an AuthProvider');
  }
  return context;
}

export function useAuthActions(): AuthActions {
  const context = useContext(AuthActionsContext);
  if (context === undefined) {
    throw new Error('useAuthActions must be used within an AuthProvider');
  }
  return context;
}

/** Combined hook for components that need both state and actions (same as before). */
export function useAuth(): AuthState & AuthActions {
  const state = useAuthState();
  const actions = useAuthActions();
  return useMemo(() => ({ ...state, ...actions }), [state, actions]);
}

export const withAuth = <P extends object>(
  Component: React.ComponentType<P>,
  requireAuth: boolean = true
): React.FC<P> => {
  return function WithAuthWrapper(props: P) {
    const { user, loading } = useAuthState();

    if (loading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600" />
        </div>
      );
    }

    if (requireAuth && !user) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
            <p className="text-gray-600 mb-6">Please sign in to access this page.</p>
            <a
              href="/login"
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Sign In
            </a>
          </div>
        </div>
      );
    }

    return <Component {...props} />;
  };
};

/**
 * Route guard for admin-only pages. Firestore/Storage rules are the real
 * security boundary; this keeps the admin UI from rendering for non-admins.
 */
export const withAdmin = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  return function WithAdminWrapper(props: P) {
    const { user, isAdmin, loading } = useAuthState();

    if (loading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600" />
        </div>
      );
    }

    if (!user || !isAdmin) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Admin Access Required</h2>
            <p className="text-gray-600 mb-6">
              {user
                ? 'Your account does not have permission to view this page.'
                : 'Please sign in with an admin account to access this page.'}
            </p>
            <a
              href={user ? '/' : '/login'}
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              {user ? 'Back to Home' : 'Sign In'}
            </a>
          </div>
        </div>
      );
    }

    return <Component {...props} />;
  };
};

export function useAdminAuth() {
  const { isAdmin, isSuperAdmin } = useAuthState();
  return {
    isAdmin,
    isSuperAdmin,
    canAccessAdmin: isAdmin,
  };
}
